import { zodResolver } from '@hookform/resolvers/zod';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { parse } from 'date-fns';
import { Helmet } from 'react-helmet';
import z from 'zod';
import { DiscoveryHistorySeeds } from './DiscoveryHistorySeeds';
import { DiscoverySeedForm } from './DiscoverySeedForm';
import type { DiscoverySeedModel } from './model/DiscoverySeedModel';

interface Props {
  model: DiscoverySeedModel;
}

export const numericDaysLimitValues = ['7', '14', '30', '90'] as const;
export type NumericDaysLimit = (typeof numericDaysLimitValues)[number];

export const daysLimitValues = [
  'all',
  'custom',
  ...numericDaysLimitValues,
] as const;
export type DaysLimit = (typeof daysLimitValues)[number];

export interface DiscoveryHistorySeedFormValues {
  daysLimit: DaysLimit;
  enabled: boolean;
  afterDate: Date | null;
}

export interface DiscoverySeedFormValues
  extends DiscoveryHistorySeedFormValues {
  ip: IPSubnetInputValueOrDraft;
}

export const DiscoverySeed = ({ model }: Props) => {
  const { control, setValue } = useCustomForm<DiscoverySeedFormValues>({
    values: {
      ip: model?.initialSeedList ?? { ips: [] },
      enabled: model?.initialDiscoveryHistorySeeds?.enabled,
      daysLimit:
        typeof model?.initialDiscoveryHistorySeeds?.daysLimit === 'number'
          ? (model?.initialDiscoveryHistorySeeds?.daysLimit.toString() as NumericDaysLimit)
          : model?.initialDiscoveryHistorySeeds?.afterDate
            ? 'custom'
            : 'all',
      afterDate: model?.initialDiscoveryHistorySeeds?.afterDate
        ? parse(
            model?.initialDiscoveryHistorySeeds?.afterDate,
            'yyyy-MM-dd',
            new Date(),
          )
        : null,
    },
    resolver: zodResolver(getValidationSchema),
  });

  return (
    <div className='-mx'>
      <Helmet title='(Optional) Provide starting points for network discovery' />
      <div className='px-sm'>
        <DiscoverySeedForm
          model={model}
          control={control}
          setValue={setValue}
        />
      </div>
      <DiscoveryHistorySeeds
        control={control}
        setValue={setValue}
        model={model}
      />
    </div>
  );
};

const getValidationSchema = z
  .object({
    ip: ipSubnetInputValueSchema,
  })
  .superRefine((data, ctx) => {
    if (hasIPSubnetInputValueError(data.ip)) {
      // Display the error message from the draft value
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: data.ip.message,
        path: ['ip'],
      });
    }
  });
